import {
  ProgramRegistrationSummaryItemType,
  Registration,
  RegistrationAtCapacityType,
  RegistrationSummaryItemWithCapacity,
  RegistrationType,
  TeamRegistrationSummaryItemType,
} from '@la/types';
import { isExistingStaff, isPlayerRegistration } from '@la/utilities';

// Program registration types only have one summary item for a given program
export const programRegistrationTypeAtCapacity = (
  summaries: RegistrationSummaryItemWithCapacity[],
  registrationType: ProgramRegistrationSummaryItemType
): boolean => {
  return !!summaries.find((summary) => summary.type === registrationType)
    ?.atCapacity;
};

// Team registration types can have multiple summary items for a given program. The count
// of teams needs to be compared to the count of registrations for that type so that we can
// determine if any of the teams have open capacity
export const teamRegistrationTypeAtCapacity = (
  summaries: RegistrationSummaryItemWithCapacity[],
  registrationType: TeamRegistrationSummaryItemType
): boolean => {
  const registeredTeamsSummary = summaries.find(
    (summary) => summary.type === ProgramRegistrationSummaryItemType.Team
  );
  const registeredTeamsCount = registeredTeamsSummary?.value ?? 0;
  const registrantsCount =
    summaries.filter(
      (summary) => summary.type === registrationType && summary.atCapacity
    ).length ?? 0;

  return registeredTeamsCount <= registrantsCount;
};

export const teamsAtCapacity = (
  summaries: RegistrationSummaryItemWithCapacity[]
): boolean => {
  return programRegistrationTypeAtCapacity(
    summaries,
    ProgramRegistrationSummaryItemType.Team
  );
};

export const playerAtCapacity = (
  summaries: RegistrationSummaryItemWithCapacity[]
): boolean => {
  return teamRegistrationTypeAtCapacity(
    summaries,
    TeamRegistrationSummaryItemType.Player
  );
};

export const staffAtCapacity = (
  summaries: RegistrationSummaryItemWithCapacity[]
): boolean => {
  return teamRegistrationTypeAtCapacity(
    summaries,
    TeamRegistrationSummaryItemType.Staff
  );
};

export const playerFreeAgentAtCapacity = (
  summaries: RegistrationSummaryItemWithCapacity[]
): boolean => {
  return programRegistrationTypeAtCapacity(
    summaries,
    ProgramRegistrationSummaryItemType.PlayerFreeAgent
  );
};

export const staffFreeAgentAtCapacity = (
  summaries: RegistrationSummaryItemWithCapacity[]
): boolean => {
  return programRegistrationTypeAtCapacity(
    summaries,
    ProgramRegistrationSummaryItemType.StaffFreeAgent
  );
};

export const allRegistrationTypesAtCapacity = (
  summaries: RegistrationSummaryItemWithCapacity[]
): boolean => {
  const capacityStatuses = [
    teamsAtCapacity(summaries),
    playerAtCapacity(summaries),
    staffAtCapacity(summaries),
    playerFreeAgentAtCapacity(summaries),
    staffFreeAgentAtCapacity(summaries),
  ];

  return capacityStatuses.every((atCapacity) => atCapacity);
};

export const getCapacityStatusAndType = (
  type: RegistrationType | undefined,
  programId: number | undefined,
  teamId: number | undefined,
  userId: number | undefined,
  userRegistrations: Registration[] | undefined,
  registrationSummaryItems: RegistrationSummaryItemWithCapacity[] | undefined
): { atCapacity: boolean; atCapacityType?: RegistrationAtCapacityType } => {
  if (
    !type ||
    !programId ||
    !userId ||
    !userRegistrations ||
    !registrationSummaryItems
  ) {
    return { atCapacity: false };
  }

  /**
   * If the player is team staff or free agent staff, check if they are already
   * registered to the team/program (respectively) and ignore the capacity
   */
  const isRegisteredStaff = isExistingStaff(
    userRegistrations,
    programId,
    userId,
    type === RegistrationType.Staff ? teamId : undefined
  );
  if (isRegisteredStaff) {
    return { atCapacity: false };
  }

  /**
   * Otherwise, check the atCapacity from the registration summary based on the type.
   */
  let atCapacity = false;
  let atCapacityType: RegistrationAtCapacityType;
  if (teamId) {
    // PLAYER and STAFF (team-based)
    teamId = parseInt(teamId.toString());
    const summaryType = isPlayerRegistration(type)
      ? TeamRegistrationSummaryItemType.Player
      : TeamRegistrationSummaryItemType.Staff;
    atCapacity = !!registrationSummaryItems.find(
      (summary) =>
        summary.type === summaryType &&
        summary.teamIdOg &&
        summary.teamIdOg === teamId
    )?.atCapacity;
    atCapacityType = RegistrationAtCapacityType.Team;
  } else {
    // PLAYER_FREE_AGENT and STAFF_FREE_AGENT (program-based)
    const summaryType = isPlayerRegistration(type)
      ? ProgramRegistrationSummaryItemType.PlayerFreeAgent
      : ProgramRegistrationSummaryItemType.StaffFreeAgent;
    atCapacity = !!registrationSummaryItems.find(
      (summary) => summary.type === summaryType
    )?.atCapacity;
    atCapacityType = RegistrationAtCapacityType.Program;
  }

  /**
   * If it is at capacity for the registration type, we also do a check to see if
   * the program/teams are actually at capacity for ALL registration types and if
   * so, adjust accordingly the capacity type.
   */
  if (
    atCapacity &&
    atCapacityType &&
    allRegistrationTypesAtCapacity(registrationSummaryItems)
  ) {
    atCapacityType = RegistrationAtCapacityType.All;
  }

  return { atCapacity, atCapacityType };
};
